import React, {useEffect, useState} from "react";
import {Col, Row} from "reactstrap";
import Dialog from "../../base/dialog";
import Divider from "@mui/material/Divider";
import Button from "../../base/button/button";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import TryAgain from "../../app-specific/try-again";
import classnames from "classnames";
import {Switch} from "../../base/switch";
import {CircularProgress, Fade} from "@mui/material";
import {NotificationChannelTypes} from "../../../../core/constants/enums";


const UpdateUserNotificationSubscriptionsModal = ({
                                                      open,
                                                      setOpen,
                                                      member,
                                                      onChange,
                                                  }) => {
    const [events, setEvents] = useState([])
    const [loading, setLoading] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Listens to the changes in open state of the dialog and with each change:
     * Loads the subscription events available for the current operator from the server.
     */
    useEffect(() => {
        if (!open) return
        getSubscriptionEvents().then()
    }, [open])

    /**
     * Fetches the list of available subscription events from the server and populates the internal list.
     */
    const getSubscriptionEvents = async () => {
        setLoading(true);
        const forApi = {
            typeIds: [NotificationChannelTypes.user, NotificationChannelTypes.userAndSystem],
            keyword: '',
        }
        const response = await Api.geAllAvailableOperatorSubscriptionChannels(forApi);
        if (!isMounted())
            return;
        const memberEventIds = member.activeSubscriptionsForCurrentOperator?.map(e => e.id) ?? [];
        if (response?.resultFlag) {
            setEvents(response.data?.map(e => ({
                ...e,
                selected: memberEventIds.includes(e.id),
                loading: false
            })) ?? []);
        }
        setLoading(false);
    }

    /**
     * Changes operators subscription for a users' specific event.
     * @param {number} eventId the id of the event
     * @param {boolean} value the value of the switch after change.
     */
    const changeOperatorSubscriptionToUserEvent = (eventId, value) => {
        setEvents(prevState => prevState.map(e => e.id === eventId ? ({...e, loading: true}) : e))
        const forApi = {
            userId: member.id,
            channelId: eventId,
            operatorId: null,
        }
        const apis = {
            "true": Api.subscribeToNotificationChannel,
            "false": Api.unSubscribeFromNotificationChannel,
        }
        apis[value.toString()](forApi).then((response) => {
            if (!isMounted()) return
            setEvents(prevState => {
                const newEvents = prevState.map(e => e.id === eventId
                    ? ({
                        ...e,
                        loading: false,
                        selected: response?.resultFlag ? !e.selected : e.selected,
                    })
                    : e)
                onChange(member.id, newEvents?.filter(e => e.selected))
                return newEvents
            })
        })
    }

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={'sm'}
        >
            <Row className='no-gutters justify-content-center align-items-start w-100'>
                <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                    <p className={`text-lg text-color-1 font-weight-semi-bold text-center`}>
                        User Behaviours Notification Subscriptions
                    </p>
                </Row>

                <Divider className='divider'/>
                <Row className='no-gutters justify-content-around w-100'>
                    <Row className='no-gutters justify-content-around w-100 mx-4'>
                        <Col xs={12} className={'my-3 mb-5 w-100'}>
                            <p className={'text-color-3 text-sm'}>
                                Please select the user related events that you want to get notified about.
                            </p>
                        </Col>
                        {
                            loading
                                ? <>
                                    {
                                        Array(4).fill(null).map((e, index) => (
                                            <div key={index} className={'loading small mx-4 mb-3 w-100'}>
                                                <div/>
                                            </div>
                                        ))
                                    }
                                </>
                                : events?.length === 0
                                    ? <TryAgain
                                        text={"There is currently no event to subscribe to."}
                                        buttonText={'Reload event list'}
                                        onClick={getSubscriptionEvents}
                                    />
                                    : events?.map((event, index) => (
                                        <Col xs={12}
                                             key={event.id}
                                             className={classnames('p-3 mx-4 mb-3 d-flex align-items-center' +
                                                 ' justify-content-between', {
                                                 'background-secondary-color-4': index % 2 === 0,
                                                 'top-border-rounded': index === 0,
                                                 'bottom-border-rounded': index + 1 === events.length,
                                             })}
                                        >
                                            <p className={'text-sm font-weight-semi-bold black mr-4'}>
                                                {event?.description ?? ''}
                                            </p>
                                            <div className={'d-flex align-items-center justify-content-end'}>
                                                <Switch
                                                    color={'primary'}
                                                    checked={event.selected ?? false}
                                                    disabled={event.loading}
                                                    onChange={(val) => changeOperatorSubscriptionToUserEvent(event.id, val)}
                                                />
                                                <Fade in={event?.loading} className={'ml-3'}>
                                                    <CircularProgress
                                                        size={20}
                                                        color={'secondary'}
                                                    />
                                                </Fade>
                                            </div>
                                        </Col>
                                    ))
                        }
                    </Row>
                    <div className='my-2 w-100'/>
                    <Divider className='divider'/>
                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Button
                                disabled={!open}
                                variant={'dialog'}
                                color={'primary'}
                                onClick={() => setOpen(false)}
                                borderRadius={'0 8px 0 8px'}
                            >
                                Close
                            </Button>
                        </Row>
                    </Row>
                </Row>
            </Row>
        </Dialog>
    );
}

export default UpdateUserNotificationSubscriptionsModal;
